    ////////////////////
// Template
////////////////////
const template = /*html*/ `
    <div class="c-product-gallery" :class="{ 'is-loaded': !isGalleryLoading }">
        <div class="c-product-gallery_spinner">
            <div class="c-product-gallery_spinner_item"></div>
            <div class="c-product-gallery_spinner_item"></div>
            <div class="c-product-gallery_spinner_item"></div>
            <div class="c-product-gallery_spinner_item"></div>
        </div>
        <div class="c-product-gallery_wrapper">
            <div
                class="c-product-gallery_wrap"
                data-scroll
                data-scroll-speed="-0.5"
                data-scroll-position="top"
            >
                <div v-if="galleryImages.length" class="c-product-gallery_carousel || swiper-container" ref="carousel">

                    <div class="swiper-wrapper">
                        <div
                            class="c-product-gallery_slide || swiper-slide"
                            v-for="(image, index) in galleryImages"
                            :key="index"
                        >
                            <div class="c-product-gallery_inner">
                                <img
                                    v-if="image.imageObj.url"
                                    class="c-product-gallery_image"
                                    :src="image.imageObj.url"
                                    :alt="image.id"
                                    :width="image.imageObj.width"
                                    :height="image.imageObj.height"
                                >
                            </div>
                        </div>
                    </div>

                </div>
            </div>

            <div class="c-product-gallery_nav" ref="galleryNav">
                <div v-if="galleryPreviews.length" class="c-product-gallery_nav_inner">
                    <ul class="c-product-gallery_nav_list">
                        <li class="c-product-gallery_nav_item" v-for="(item, index) in galleryPreviews" ref="galleryBtnItem">
                            <button
                                class="c-button -gallery"
                                v-bind:style="{ 'background-image': 'url(' + item.imageObj.url + ')' }"
                                @click="onNavClick(index)"
                                @mouseenter="onNavMouseEnter(index)"
                                @mouseleave="onNavMouseLeave(index)"
                            >
                                <span class="u-screen-reader-text">Slide {{ item }}</span>
                            </button>
                        </li>
                    </ul>
                    <span class="c-product-gallery_nav_indicator"></span>
                </div>
            </div>
        </div>

    </div>
`;


////////////////////
// Scripts
////////////////////
import { Swiper, Pagination, Navigation } from 'swiper';

import { loadImage } from "../../utils/image";
import productGalleryFormatMixin from "../mixins/product-gallery-format-mixin";
import productGalleryNavMixin from '../mixins/product-gallery-nav-mixin';

const MIN_LOADING_TIME = 0;

export default function VueProductGallery(moduleInstance) {

    Vue.component("product-gallery", {

        template,

        mixins: [productGalleryFormatMixin, productGalleryNavMixin],

        data() {
            return {
                galleryImages: [],
                galleryPreviews: [],
                isGalleryLoading: false
            };
        },

        props: {
            variantsJson: {
                type: String,
                required: true
            },
            defaultCurrentVariantId: {
                type: String,
                required: true
            },
            imagesJson: {
                type: String,
                required: true
            }
        },

        created() {

            Swiper.use([Pagination, Navigation]);

            // Parse
            this.variantItems = this.variantsJson ? JSON.parse(this.variantsJson) : []
            this.images = this.imagesJson ? JSON.parse(this.imagesJson) : []

            const currentVariantItemId = this.defaultCurrentVariantId ? JSON.parse(this.defaultCurrentVariantId) : null

            // Prepare array to store loaded images
            this.loadedImages = []
            this.isFirstLoading = true

            // 1 - Get images list and slugify alt attribute
            this.setProductImagesData(this.images)

            // 2 - Get products variants, slugify options then create for each variant an array of images
            this.allProductVariants = this.setProductVariantsData(this.variantItems, this.images)

            // 3 - Set current product variant
            this.currentProductVariant = this.allProductVariants.find(variant => variant.id === currentVariantItemId)

            // 4 - Set gallery
            this.updateGallery()
        },

        beforeDestroy() {
            this.swiperInstance?.destroy?.()
        },

        methods: {

            ///////////////
            // Vue Logic
            ///////////////
            /**
            * Load and populate gallery images
            */
            async updateGallery() {

                // Get variant images
                const currentProductImages = this.currentProductVariant.images

                // Clear previous timeout
                this.loadingTimeout && clearTimeout(this.loadingTimeout)

                // Set loading state
                this.isGalleryLoading = true

                // Get all product images url
                const images = []
                const previews = []
                let index = 0
                while (index < currentProductImages.length) {
                    const currentProductImagesObj = currentProductImages[index]
                    previews.push(currentProductImagesObj.preview)
                    images.push(currentProductImagesObj.image)
                    index++
                }

                // New images to load
                const loadedImagesIds = this.loadedImages.map(imageObj => imageObj.id)
                const imagesToLoad = images.filter(imageUrl => loadedImagesIds.indexOf(imageUrl) === -1)
                const previewsToLoad = previews.filter(imageUrl => loadedImagesIds.indexOf(imageUrl) === -1)

                // Log images to load
                // console.group("IMAGES TO LOAD");
                // console.log("Images", imagesToLoad);
                // console.log("Previews", previewsToLoad);
                // console.groupEnd();

                // Load main images
                if (imagesToLoad.length) {
                    await this.loadImages(imagesToLoad)
                }

                // Load previews images
                if (previewsToLoad.length) {
                    await this.loadImages(previewsToLoad)
                }

                // Timeout for loading animation
                this.loadingTimeout = setTimeout(() => {

                    // Reset gallery images / previews
                    this.galleryImages = []
                    this.galleryPreviews = []

                    // Populate gallery arrays
                    currentProductImages.forEach(image => {
                        const targetImage = this.loadedImages.find(loadedImage => loadedImage.id === image.image)
                        const targetPreview = this.loadedImages.find(loadedImage => loadedImage.id === image.preview)

                        this.galleryImages.push(targetImage)
                        this.galleryPreviews.push(targetPreview)
                    })

                    // Call hook
                    this.onUpdateGallery?.()

                    setTimeout(() => {
                        // Set state
                        this.isFirstLoading = false
                        this.isGalleryLoading = false
                    }, 200)

                }, MIN_LOADING_TIME)
            },

            /**
            * Promise to load image
            */
            async loadImages(imageArr) {
                const loadingPromisesArr = []
                let imageIndex = 0;

                while (imageIndex < imageArr.length) {
                    const currentUrlToLoad = imageArr[imageIndex]
                    loadingPromisesArr.push(loadImage(currentUrlToLoad))
                    imageIndex++
                }
                const loadedImages = await Promise.all(loadingPromisesArr)

                // Store loaded images
                let loadedImagesIndex = 0
                while (loadedImagesIndex < loadedImages.length) {
                    const currentLoadedImage = loadedImages[loadedImagesIndex]
                    this.loadedImages.push({
                        id: currentLoadedImage.url,
                        imageObj: currentLoadedImage
                    })
                    loadedImagesIndex++
                }
            },

            ///////////////
            // Callbacks
            ///////////////
            onProductVariantChange(variantId) {

                // First loading
                if (this.isFirstLoading) {
                    this.oldVariantId = variantId
                    return
                }

                // TODO: If variant doesn't exist do something
                if (!variantId) return

                // If already current variant id
                if (this.oldVariantId === variantId) return

                // Set current product variant
                this.currentProductVariant = this.allProductVariants.find(variant => variant.id === variantId)

                // Update gallery
                this.updateGallery()

                // Set old variant
                this.oldVariantId = variantId
            },

            onUpdateGallery() {
                this.swiperInstance?.destroy?.()

                this.$nextTick(() => {
                    this.initSwiper()
                    this.updateGalleryNav()
                })
            },

            onResize() {
                //console.log('on resize gallery')
                this?.onNavResize?.()
            },

            ///////////////
            // Swiper
            ///////////////
            initSwiper() {
                if (!this.$refs.carousel) return

                this.swiperInstance = new Swiper(this.$refs.carousel, {
                    slidesPerView: 1,
                    grabCursor: true,
                    //loop: true,
                    speed: 900,
                    enabled: this.galleryImages.length > 1 ? true : false,
                    on: {
                        activeIndexChange: (swiper) => {
                            this?.setNavIndex?.(swiper.activeIndex)
                        },
                    },
                });
            },
        }
    });
}
